/**
 * 音色 —— 表单里存什么、界面显示什么。
 *
 * **「哪些音色能用」的真源在后端接口 `GET /api/aigc/voices`**，接口那一头又是
 * 当前引擎自己的清单（默认引擎下是 `minimax-tts` skill 里的官方音色快照，303 条）。
 * 前端不维护第二份清单：一旦硬编码，skill 删掉一条音色、页面照样把它发出去，
 * 症状是**用户选了 A、出来的声音是 B**，而任务状态、画面、口型全绿 ——
 * 只有成片里听得出。
 *
 * ⚠️ **`form.voice` 存的就是上游音色 ID**（如 `Chinese (Mandarin)_Warm_Girl`）。
 *
 * 旧版存的是本地编的短 id（`female-warm`），再由 `VOICE_OPTIONS[].tts` 翻成上游 ID。
 * 那层映射 2026-10-06 删掉了 —— 它的存在只服务「界面文案想写得更长一点」，
 * 代价是多一份表、多一处「哪一份才是发给上游的」的歧义。现在名称由接口下发。
 * 历史草稿里的 5 个旧 id 由 `LEGACY_VOICE_IDS` 一次性翻译（见下）。
 */
import type { VoiceGroup, VoiceInfo } from '@/api/types'

export type { VoiceGroup, VoiceInfo }

/**
 * 默认音色 —— **必须等于后端「默认引擎」的默认音色**。
 *
 * 后端 `app/tts_voices.py` 的引擎表：`ENGINE_DEFAULTS[ENGINE_DEFAULT]`。
 * 2026-10-08 起默认引擎是 `minimax-direct`（绕过挂掉的 apilio 网关直连官方），
 * 默认音色是 `Chinese (Mandarin)_Warm_Girl`（官方名「温暖少女」）。
 * 后端 `scripts/test_tts_voices.py` 跨仓库钉这一条 —— 它读的就是本文件。
 *
 * ⚠️ 这个值现在**同时存在于两套引擎的清单里**（apilio 网关那 88 条里也有
 * 同一个 ID），所以「选了 A 出来 B」在这里不会表现为「找不到这个音色」，
 * 而是**同一个 ID 由两套完全不同的上游合成**（网关一档 / 官方一档）。
 * 换句话说：这个文件验不了「哪条链路在跑」，那要靠后端下发的 `engine`。
 */
export const DEFAULT_VOICE_ID = 'Chinese (Mandarin)_Warm_Girl'

/**
 * 接口拉不到时的**最小可用集**（离线 / 后端没起）。
 *
 * ⚠️ 它属于**默认引擎**，不是「随便几条好听的」：能用哪些音色是上游的事，
 * 而不同引擎是**完全不同的 ID 集合**（直连官方是 `Chinese (Mandarin)_*` 那套
 * 303 条，openai 是 `shimmer` 这一套 13 条，apilio 网关那 88 条又自成一套）。
 * 接口拉不到时前端无从得知引擎是什么（正是因为它拉不到），所以只能硬编码
 * 默认引擎那一套 —— **默认引擎一换，这份清单必须跟着换**，否则离线时会
 * 「下拉列 A 引擎的音色、后端按 B 引擎合成」，也就是选了 A 出来 B。
 *
 * 只列 5 条：其余的正确性由后端那份清单背书，在这里补几条「听着像对的」ID
 * 就是重新引入幽灵音色。后端 `scripts/test_tts_voices.py` 会读这个文件逐条比对，
 * 而且**路径错时会报错而不是跳过**（那条断言曾经因为路径写错静默停摆）。
 *
 * ⚠️ 展示名**照抄官方 `voice_name`**（后端下发什么就写什么），别自己起中文名：
 * 两处各起一个名字，用户看到的和订单里记的就是两个东西。
 */
export const FALLBACK_VOICE_GROUPS: VoiceGroup[] = [
  {
    label: '常用（中文口播）',
    voices: [
      { id: 'Chinese (Mandarin)_Warm_Girl', name: '温暖少女' },
      { id: 'Chinese (Mandarin)_Male_Announcer', name: '播报男声' },
      { id: 'Chinese (Mandarin)_Warm_Bestie', name: '温暖闺蜜' },
      { id: 'female-yujie', name: '御姐音色' },
      { id: 'male-qn-jingying', name: '精英青年音色' },
    ],
  },
]

/**
 * 旧口径 → **minimax 引擎**音色 ID 的**一次性**翻译表。
 *
 * 只服务历史草稿：2026-10-06 之前 `form.voice` 存的是这 5 个短 id。
 * 不翻的话，老草稿在新列表里找不到对应项 → 下拉显示成第一条音色，
 * **而用户当初选的确实是另一条** —— 静默换声音，页面上完全看不出来。
 *
 * ⚠️ 表要**全覆盖**（旧口径就这 5 个，一个都不能少），且**不许留兜底**：
 * 翻不出来的值就按「没选过」处理（回落默认），而不是拿一个近似值顶上去。
 *
 * ⚠️ 默认引擎是 `minimax-direct`，这 5 个翻译结果里有 4 个**不在**官方快照里
 * （`presenter_female` / `Chinese (Mandarin)_Reliable_Executive` 这类是网关那套
 * 的旧 ID），所以会被 `pickVoiceId` 再回落到默认音色 —— 下拉里看得见。
 * 留在表里是因为切回 `minimax` 引擎后它重新生效，而历史草稿仍在。
 */
export const LEGACY_VOICE_IDS: Record<string, string> = {
  'female-warm': 'Chinese (Mandarin)_Warm_Girl',
  'female-lively': 'female-tianmei',
  'male-steady': 'Chinese (Mandarin)_Reliable_Executive',
  'male-young': 'Chinese (Mandarin)_Refreshing_Young_Man',
  'female-anchor': 'presenter_female',
}

/** 草稿里的值 → 当前口径的音色 ID（只翻译旧口径，其余原样返回） */
export function voiceIdOf(raw: string): string {
  return LEGACY_VOICE_IDS[raw] ?? raw
}

/**
 * 草稿里的值 → **此刻真能发出去的那个音色 ID**。
 *
 * 比 `voiceIdOf` 多一层「这个 ID 在当前清单里到底存不存在」的判定，
 * 因为 2026-10-06 起清单会**换引擎**（MiniMax speech 在 apilio 上整体 500，
 * 临时切到 `gpt-4o-mini-tts`，音色 ID 完全另一套）：
 *
 * - 草稿里存着 `Chinese (Mandarin)_Warm_Girl`，切到 openai 引擎后它不在清单里，
 *   若原样发出去，上游会挑一个默认音色 —— 也就是**选了 A 出来 B**；
 * - 所以这里回落到**接口下发的 `default`**（本引擎自己的默认音色）。
 *
 * ⚠️ 回落**不是静默**的：下拉的 `value` 绑的就是这个派生值，所以回落之后
 * 界面显示的就是实际会发出去的那一个 —— 用户看得见「声音被换了」。
 * 这正是「界面必须报真正会发出去的那一份」的落地方式。
 *
 * 逐级回落：草稿值（含旧口径翻译）→ 接口默认 → 清单第一条 → `DEFAULT_VOICE_ID`。
 */
export function pickVoiceId(raw: string, groups: VoiceGroup[], defaultId?: string): string {
  const mapped = voiceIdOf(raw)
  if (findVoice(groups, mapped)) return mapped
  if (defaultId && findVoice(groups, defaultId)) return defaultId
  return groups[0]?.voices[0]?.id ?? DEFAULT_VOICE_ID
}

/** 按 ID 找音色（找不到返回 undefined，由调用方决定怎么显示） */
export function findVoice(groups: VoiceGroup[], id: string): VoiceInfo | undefined {
  for (const g of groups) {
    const hit = g.voices.find((v) => v.id === id)
    if (hit) return hit
  }
  return undefined
}

/** 全部音色（扁平），用于「这个 ID 到底存不存在」的判断 */
export function flattenVoices(groups: VoiceGroup[]): VoiceInfo[] {
  return groups.flatMap((g) => g.voices)
}
